import { useState } from 'react';
import { Link, useRoute } from 'wouter';
import { ApiError } from '../lib/api';
import { useAllGraphNodes, useGraph, useLivePipeline, useMusicLibrary, useRunDetails, useRuns, useTriggerRun, type AgentRun, type LlmCall, type PersistedPipelineEvent } from '../lib/pipeline';
import { AgentInspector } from '../components/AgentInspector';
import { useSetupOutputs } from '../lib/setup';
import { getSetupReadiness } from '../lib/setupState';
import { PipelineGraphView } from '../components/PipelineGraph';
import { ProjectHeader } from '../components/ProjectHeader';
import { NextStep } from '../components/NextStep';

export function PipelinePage() {
  const [, paramsGenerator] = useRoute('/projects/:id/generator');
  const [, paramsLegacy] = useRoute('/projects/:id/pipeline');
  const params = paramsGenerator ?? paramsLegacy;
  const projectId = params?.id;
  // graph is swapped based on the format selector below — the reel content
  // team has different post-Editor stages from the carousel team, so the
  // diagram needs to match what the operator is about to trigger.
  const [pickedFormat, setPickedFormat] = useState<'carousel' | 'single_image' | 'reel_vertical'>('carousel');
  const graph = useGraph(pickedFormat === 'reel_vertical' ? 'reel' : 'runtime');
  const runs = useRuns(projectId);
  const { nodeStatus, lastEvent, events } = useLivePipeline(projectId);
  const trigger = useTriggerRun(projectId);
  const triggerError = trigger.error instanceof ApiError ? trigger.error.message : null;
  const setup = useSetupOutputs(projectId);
  const setupReadiness = getSetupReadiness(setup.data);
  const setupReady = setupReadiness.ready;
  const runtimeRuns = runs.data?.runs.filter((r) => r.pipeline === 'runtime') ?? [];
  const isRuntimeRunning = runtimeRuns.some((r) => r.status === 'running');
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [hint, setHint] = useState('');
  const [testReelOn, setTestReelOn] = useState(false);
  // v16 — reel audio mode + music track pick. Per-run override; when
  // unset, the orchestrator falls back to the brand kit's setting.
  const [reelAudioMode, setReelAudioMode] = useState<'vo' | 'music' | undefined>(undefined);
  const [reelMusicTrackId, setReelMusicTrackId] = useState<string | undefined>(undefined);
  const musicLibrary = useMusicLibrary();
  const format = pickedFormat;
  const setFormat = setPickedFormat;
  const HINT_MAX = 500;
  // Test-mode toggle is only meaningful for reels.
  const testSeconds = format === 'reel_vertical' && testReelOn ? 5 : undefined;

  const eventsByRun = new Map<string, typeof events>();
  for (const e of events) {
    const list = eventsByRun.get(e.runId);
    if (list) list.push(e);
    else eventsByRun.set(e.runId, [e]);
  }
  const runIdsInOrder = Array.from(eventsByRun.keys()).reverse();

  if (!projectId) return null;

  return (
    <div className="min-h-screen">
      <ProjectHeader projectId={projectId} page="GENERATOR" />

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-6 space-y-5">
        <NextStep projectId={projectId} />
        <section className="rounded-[1.75rem] border border-border-color bg-surface p-4 md:p-6 overflow-hidden relative">
          <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary/10 to-transparent pointer-events-none" />
          <div className="relative space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 max-w-3xl">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-accent-cyan mb-3">Content</p>
                <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight">Brief your content team.</h1>
                <p className="mt-3 text-sm text-muted leading-relaxed">
                  Hand them the next post. Add a one-sentence steer below if you have something specific in mind, or leave it empty and they'll draw from the brand brief.
                </p>
                {!setupReady && (
                  <p className="mt-3 text-xs text-accent-amber">
                    Strategy is not ready yet. Missing: {setupReadiness.missing.join(', ') || 'brand memory'}.
                  </p>
                )}
              </div>
            </div>

            {setupReady && (
              <div className="space-y-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-2">Format</p>
                  <FormatSelector value={format} onChange={setFormat} disabled={trigger.isPending || isRuntimeRunning} />
                  {format === 'reel_vertical' && (
                    <div className="mt-3 space-y-2">
                      <label className="inline-flex items-center gap-2 text-xs text-muted cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={testReelOn}
                          disabled={trigger.isPending || isRuntimeRunning}
                          onChange={(e) => setTestReelOn(e.target.checked)}
                          className="accent-accent-cyan"
                        />
                        <span className="font-mono uppercase tracking-wider text-[10px]">
                          Quick test (5s reel)
                        </span>
                        <span className="text-[10px]">— skip full-length for fast visual iteration</span>
                      </label>

                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-muted">Audio</span>
                        <div className="inline-flex rounded-lg border border-border-color overflow-hidden">
                          {([
                            { value: undefined, label: 'Brand default' },
                            { value: 'vo' as const, label: 'Voiceover' },
                            { value: 'music' as const, label: 'Music only' },
                          ]).map((opt) => (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() => setReelAudioMode(opt.value)}
                              disabled={trigger.isPending || isRuntimeRunning}
                              className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                                reelAudioMode === opt.value
                                  ? 'bg-accent-cyan/15 text-accent-cyan'
                                  : 'text-muted hover:text-text-primary'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        {reelAudioMode === 'music' && (
                          <select
                            value={reelMusicTrackId ?? ''}
                            onChange={(e) => setReelMusicTrackId(e.target.value || undefined)}
                            disabled={trigger.isPending || isRuntimeRunning}
                            className="bg-surface-2 border border-border-color rounded-lg px-3 py-1 text-[11px] text-text-primary font-mono uppercase tracking-wider"
                          >
                            <option value="">Use brand kit pick</option>
                            {(musicLibrary.data ?? []).map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.label} ({t.moods.join(', ')})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid lg:grid-cols-[1fr_auto] gap-4 items-end">
                  <div>
                    <label htmlFor="hint" className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted block mb-2">
                      Brief for this post (optional)
                    </label>
                    <textarea
                      id="hint"
                      value={hint}
                      onChange={(e) => setHint(e.target.value.slice(0, HINT_MAX))}
                      rows={2}
                      placeholder="e.g. Focus on the dread of forgetting which resume version you sent last week. Or leave empty — your team picks from the brand brief."
                      disabled={trigger.isPending || isRuntimeRunning}
                      className="w-full bg-surface-2 border border-border-color rounded-xl px-3 py-2 text-sm text-text-primary placeholder-muted focus:outline-none focus:border-accent-cyan transition-colors resize-y disabled:opacity-50"
                    />
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted">
                      {hint.length}/{HINT_MAX} chars
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {triggerError && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-red">Could not start: {triggerError}</span>}
                    <button
                      onClick={() => trigger.mutate({
                        hint: hint.trim() || undefined,
                        format,
                        testSeconds,
                        audioMode: format === 'reel_vertical' ? reelAudioMode : undefined,
                        musicTrackId: format === 'reel_vertical' && reelAudioMode === 'music' ? reelMusicTrackId : undefined,
                      })}
                      disabled={trigger.isPending || isRuntimeRunning}
                      className="btn-bracket bg-primary text-white px-5 py-3 text-sm hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center gap-2 rounded-xl"
                    >
                      <span className="material-symbols-outlined text-[18px]">auto_awesome</span>
                      {trigger.isPending || isRuntimeRunning ? 'Generating' : 'Generate post'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!setupReady && (
              <div className="flex items-center gap-3 flex-wrap">
                {triggerError && <span className="font-mono text-[10px] uppercase tracking-wider text-accent-red">Could not start: {triggerError}</span>}
                <Link href={`/projects/${projectId}/setup`} className="btn-bracket border border-border-color text-muted px-4 py-2 hover:text-text-primary hover:border-text-primary transition-colors rounded-xl">Finish strategy</Link>
              </div>
            )}
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard label="Current run">
            {lastEvent ? (
              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <span className="font-medium">{lastEvent.agentName}</span>
                  <span className="ml-2 font-mono text-xs text-muted">{lastEvent.eventType}</span>
                </div>
                <span className="text-xs text-muted whitespace-nowrap">{events.length} steps</span>
              </div>
            ) : <p className="text-xs text-muted">No run is active in this browser session.</p>}
          </StatCard>
          <StatCard label="Posts created">
            <span className="font-display text-2xl font-bold">{runtimeRuns.filter((r) => r.status === 'completed').length}</span>
          </StatCard>
          <StatCard label="Team spend this month">
            <span className="font-mono text-lg">${runs.data?.monthlySpendUsd?.toFixed(4) ?? '—'}</span>
          </StatCard>
        </section>

        <section>
          <div className="flex items-baseline justify-between mb-3 gap-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Content team</p>
              <h2 className="font-display text-2xl font-bold tracking-tight">Your content team at work</h2>
            </div>
            {triggerError && <span className="text-xs text-accent-red">{triggerError}</span>}
          </div>
          {graph.isLoading && <p className="text-sm text-muted">Loading team…</p>}
          {graph.data && <PipelineGraphView graph={graph.data} nodeStatus={nodeStatus} />}
          <p className="text-xs text-muted mt-2">
            Read left to right. Each specialist receives something specific from the one before, improves it, and hands it to the next.
          </p>
        </section>

        {events.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">Content team activity · this session</h2>
            <div className="rounded-lg border border-border-color overflow-hidden divide-y divide-border-color max-h-96 overflow-y-auto">
              {runIdsInOrder.map((runId) => {
                const runEvents = eventsByRun.get(runId)!;
                const startedAt = runEvents[0]?.at;
                return (
                  <div key={runId} className="p-3 bg-surface-2/40">
                    <div className="flex items-baseline justify-between text-xs mb-2">
                      <span className="font-mono text-muted">run {runId.slice(0, 8)}</span>
                      <span className="text-muted">{startedAt ? new Date(startedAt).toLocaleString() : ''}</span>
                    </div>
                    <ol className="space-y-0.5">
                      {runEvents.map((e, i) => (
                        <li key={`${e.nodeId}-${e.eventType}-${i}`} className="text-xs grid grid-cols-[80px_120px_1fr_auto] gap-2 font-mono">
                          <span className="text-muted">{new Date(e.at).toLocaleTimeString([], { hour12: false })}</span>
                          <span className={e.eventType === 'finished' ? 'text-accent-emerald' : e.eventType === 'failed' ? 'text-accent-red' : e.eventType === 'started' ? 'text-accent-cyan' : 'text-muted'}>{e.eventType}</span>
                          <span className="font-sans font-medium text-text-primary">{e.agentName}</span>
                          <span className="text-muted">{e.durationMs != null ? `${e.durationMs}ms` : ''}</span>
                          {e.errorMessage && <span className="col-span-4 text-accent-red font-sans pl-[210px] break-all">↳ {e.errorMessage}</span>}
                        </li>
                      ))}
                    </ol>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {runtimeRuns.length > 0 && (
          <section>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">Recent content team runs</h2>
            <div className="rounded-lg border border-border-color overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Run</th>
                    <th className="text-left px-3 py-2 font-medium">Started</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Issue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-color">
                  {runtimeRuns.map((r) => (
                    <RunHistoryRow
                      key={r.id}
                      run={r}
                      projectId={projectId}
                      expanded={expandedRunId === r.id}
                      onToggle={() => setExpandedRunId((current) => current === r.id ? null : r.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function RunHistoryRow({
  run,
  projectId,
  expanded,
  onToggle,
}: {
  run: AgentRun;
  projectId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const details = useRunDetails(projectId, run.id, expanded);
  const nodes = useAllGraphNodes();
  const tone = run.status === 'completed' ? 'text-accent-emerald' : run.status === 'failed' ? 'text-accent-red' : 'text-accent-cyan';
  const totalCost = details.data?.calls.reduce((sum, call) => sum + Number(call.usdCost ?? 0), 0) ?? 0;
  const [inspecting, setInspecting] = useState<PersistedPipelineEvent | null>(null);

  // Show inspector openings only on the meaningful events: the agent's
  // "finished" row (post-work) or its "failed" row. "started" is just a
  // begin marker — no output to inspect.
  function canInspect(e: PersistedPipelineEvent): boolean {
    return e.eventType === 'finished' || e.eventType === 'failed';
  }

  const llmCallsForInspecting: LlmCall[] = inspecting
    ? (details.data?.calls ?? []).filter((c) => c.runId === inspecting.runId && c.nodeId === inspecting.nodeId)
    : [];
  const nodeForInspecting = inspecting ? nodes.data?.[inspecting.nodeId] ?? null : null;

  return (
    <>
      <tr className="hover:bg-surface-2">
        <td className="px-3 py-2">
          <button onClick={onToggle} className="flex items-center gap-2 font-mono text-xs text-text-primary hover:text-accent-cyan">
            <span className="material-symbols-outlined text-[16px]">{expanded ? 'expand_less' : 'expand_more'}</span>
            {run.id.slice(0, 8)}
          </button>
        </td>
        <td className="px-3 py-2 text-xs text-muted">{new Date(run.startedAt).toLocaleString()}</td>
        <td className="px-3 py-2 text-xs"><span className={tone}>{run.status}</span></td>
        <td className="px-3 py-2 text-xs text-accent-red max-w-md">{run.error ? <code className="break-all whitespace-pre-wrap">{run.error}</code> : ''}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} className="bg-background-dark/70 px-3 py-3">
            {details.isLoading && <p className="text-xs text-muted">Loading run details…</p>}
            {details.error && <p className="text-xs text-accent-red">Could not load run details.</p>}
            {details.data && (
              <div className="space-y-3">
                <div className="grid gap-2 sm:grid-cols-4 text-xs">
                  <RunMeta label="Started" value={new Date(details.data.run.startedAt).toLocaleString()} />
                  <RunMeta label="Finished" value={details.data.run.finishedAt ? new Date(details.data.run.finishedAt).toLocaleString() : 'Still running'} />
                  <RunMeta label="Duration" value={runDuration(details.data.run.startedAt, details.data.run.finishedAt)} />
                  <RunMeta label="Spend" value={`$${totalCost.toFixed(4)}`} />
                </div>
                <ol className="rounded-lg border border-border-color overflow-hidden divide-y divide-border-color">
                  {details.data.events.map((event) => (
                    <RunEventRow
                      key={event.id}
                      event={event}
                      canInspect={canInspect(event)}
                      onInspect={() => setInspecting(event)}
                    />
                  ))}
                </ol>
              </div>
            )}
          </td>
        </tr>
      )}
      <AgentInspector
        open={!!inspecting}
        onClose={() => setInspecting(null)}
        event={inspecting}
        node={nodeForInspecting}
        llmCalls={llmCallsForInspecting}
      />
    </>
  );
}

function RunMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border-color bg-surface p-2">
      <p className="font-mono text-[9px] uppercase tracking-wider text-muted">{label}</p>
      <p className="mt-1 text-xs text-text-primary">{value}</p>
    </div>
  );
}

function RunEventRow({
  event,
  canInspect,
  onInspect,
}: {
  event: PersistedPipelineEvent;
  canInspect: boolean;
  onInspect: () => void;
}) {
  const tone =
    event.eventType === 'finished' ? 'text-accent-emerald' :
      event.eventType === 'failed' ? 'text-accent-red' :
        event.eventType === 'started' ? 'text-accent-cyan' :
          'text-muted';
  const hasPayload = event.input != null || event.output != null;

  return (
    <li className="bg-surface/50">
      <div className="grid grid-cols-[92px_96px_minmax(0,1fr)_auto_auto] gap-2 px-3 py-2 text-xs items-baseline">
        <span className="font-mono text-muted">{new Date(event.at).toLocaleTimeString([], { hour12: false })}</span>
        <span className={`font-mono uppercase ${tone}`}>{event.eventType}</span>
        <span className="font-medium text-text-primary">{event.agentName}</span>
        <span className="font-mono text-muted">{event.durationMs != null ? `${event.durationMs}ms` : ''}</span>
        {canInspect ? (
          <button
            onClick={onInspect}
            title="Open Agent Inspector"
            className="font-mono text-[10px] uppercase tracking-wider text-accent-cyan hover:text-text-primary transition-colors flex items-center gap-1"
          >
            <span className="material-symbols-outlined text-[14px]">visibility</span>
            Inspect
          </button>
        ) : (
          <span />
        )}
        {event.errorMessage && <code className="col-span-5 text-accent-red whitespace-pre-wrap break-all">↳ {event.errorMessage}</code>}
      </div>
      {hasPayload && (
        <details className="border-t border-border-color/60 px-3 pb-3">
          <summary className="cursor-pointer py-2 font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text-primary">
            Step payload (compact summary — open Inspect for the full view)
          </summary>
          <div className="grid gap-2 md:grid-cols-2">
            {event.input != null && <JsonBlock label="Received (summary)" value={event.input} />}
            {event.output != null && <JsonBlock label="Produced" value={event.output} />}
          </div>
        </details>
      )}
    </li>
  );
}

function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <p className="mb-1 font-mono text-[9px] uppercase tracking-wider text-muted">{label}</p>
      <pre className="max-h-56 overflow-auto rounded border border-border-color bg-background-dark p-2 text-[11px] leading-relaxed text-muted scroll-thin">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function runDuration(startedAt: string, finishedAt: string | null): string {
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const ms = Math.max(0, end - new Date(startedAt).getTime());
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function FormatSelector({
  value,
  onChange,
  disabled,
}: {
  value: 'carousel' | 'single_image' | 'reel_vertical';
  onChange: (v: 'carousel' | 'single_image' | 'reel_vertical') => void;
  disabled: boolean;
}) {
  const options: Array<{
    id: 'carousel' | 'single_image' | 'reel_vertical';
    label: string;
    icon: string;
    hint: string;
  }> = [
    { id: 'carousel', label: 'Carousel', icon: 'view_carousel', hint: 'Multi-slide post' },
    { id: 'single_image', label: 'Single image', icon: 'image', hint: 'One 1080×1080 slide' },
    { id: 'reel_vertical', label: 'Reel', icon: 'movie', hint: '60–90s vertical video with VO' },
  ];
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((opt) => {
        const selected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-colors text-sm disabled:opacity-50 ${
              selected
                ? 'border-accent-cyan bg-accent-cyan/10 text-text-primary'
                : 'border-border-color bg-surface-2 text-muted hover:text-text-primary hover:border-text-primary/40'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{opt.icon}</span>
            <span className="text-left">
              <span className="block font-medium leading-tight">{opt.label}</span>
              <span className="block font-mono text-[10px] uppercase tracking-wider text-muted">{opt.hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border-color bg-surface p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted mb-1.5">{label}</p>
      {children}
    </div>
  );
}
